import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { rolesApi } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, Search, Shield } from "lucide-react";

// Define available permissions
const permissionGroups = [
  {
    name: "Recepción",
    permissions: [
      { id: "reception.create_patient", label: "Crear pacientes" },
      { id: "reception.edit_patient", label: "Editar pacientes" },
      { id: "reception.create_order", label: "Crear órdenes" },
      { id: "reception.edit_order", label: "Editar órdenes" },
      { id: "reception.capture_results", label: "Capturar resultados" },
      { id: "reception.validate_results", label: "Validar resultados" },
      { id: "reception.print_results", label: "Imprimir resultados" },
    ],
  },
  {
    name: "Catálogos",
    permissions: [
      { id: "catalogs.view", label: "Ver catálogos" },
      { id: "catalogs.create", label: "Crear registros" },
      { id: "catalogs.edit", label: "Editar registros" },
      { id: "catalogs.delete", label: "Eliminar registros" },
    ],
  },
  {
    name: "Reportes",
    permissions: [
      { id: "reports.view", label: "Ver reportes" },
      { id: "reports.export", label: "Exportar reportes" },
    ],
  },
  {
    name: "Administración",
    permissions: [
      { id: "admin.settings", label: "Configuración del sistema" },
      { id: "admin.users", label: "Gestionar usuarios" },
      { id: "admin.roles", label: "Gestionar roles" },
      { id: "admin.backup", label: "Respaldo de datos" },
    ],
  },
];

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await rolesApi.getAll();
      setRoles(response.data);
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Error al cargar roles");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: [...(role.permissions || [])],
      });
    } else {
      setSelectedRole(null);
      setFormData({
        name: "",
        description: "",
        permissions: [],
      });
    }
    setShowDialog(true);
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter((p) => p !== permissionId),
    }));
  };

  const handleGroupSelectAll = (group, checked) => {
    const groupPermissionIds = group.permissions.map((p) => p.id);
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...new Set([...prev.permissions, ...groupPermissionIds])]
        : prev.permissions.filter((p) => !groupPermissionIds.includes(p)),
    }));
  };

  const isGroupFullySelected = (group) => {
    return group.permissions.every((p) => formData.permissions.includes(p.id));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("El nombre del rol es requerido");
      return;
    }

    try {
      if (selectedRole) {
        await rolesApi.update(selectedRole.id, formData);
        toast.success("Rol actualizado");
      } else {
        await rolesApi.create(formData);
        toast.success("Rol creado");
      }
      setShowDialog(false);
      fetchRoles();
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error("Error al guardar rol");
    }
  };

  const handleDelete = async () => {
    try {
      await rolesApi.delete(selectedRole.id);
      toast.success("Rol eliminado");
      setShowDeleteDialog(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error("Error al eliminar rol");
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Roles y Permisos
          </h1>
          <p className="text-muted-foreground">
            Configure los roles y permisos de acceso al sistema
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Rol
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5" />
              Roles del Sistema ({filteredRoles.length})
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar rol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredRoles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No hay roles configurados</p>
              <Button
                variant="link"
                onClick={() => handleOpenDialog()}
                className="mt-2"
              >
                Crear primer rol
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Permisos</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {role.name}
                        {role.is_system && (
                          <Badge variant="secondary" className="text-xs">
                            Sistema
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {role.description}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {role.permissions?.length || 0} permisos
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(role)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {!role.is_system && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedRole(role);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRole ? "Editar Rol" : "Nuevo Rol"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Rol *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Supervisor"
                disabled={selectedRole?.is_system}
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción del rol..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Permisos</Label>
              <Accordion type="multiple" className="w-full">
                {permissionGroups.map((group) => (
                  <AccordionItem key={group.name} value={group.name}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isGroupFullySelected(group)}
                          onCheckedChange={(checked) =>
                            handleGroupSelectAll(group, checked)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span>{group.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {group.permissions.filter((p) =>
                            formData.permissions.includes(p.id)
                          ).length}
                          /{group.permissions.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pl-8">
                        {group.permissions.map((permission) => (
                          <div
                            key={permission.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={permission.id}
                              checked={formData.permissions.includes(permission.id)}
                              onCheckedChange={(checked) =>
                                handlePermissionChange(permission.id, checked)
                              }
                            />
                            <label
                              htmlFor={permission.id}
                              className="text-sm cursor-pointer"
                            >
                              {permission.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {selectedRole ? "Guardar Cambios" : "Crear Rol"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar rol?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el rol "
              {selectedRole?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
